import { useCallback, useEffect, useMemo, useRef } from "react";

import {
  candidateNotesStatus,
  fillCandidateNotes,
} from "../../../utils/extension";
import { useSendGAEvent } from "../../../utils/googleAnalytics";
import { onExtensionMessage } from "../../../utils/sendExtensionMessage";
import {
  NotesForScorecardQuery,
  useNotesForScorecardLazyQuery,
} from "../../graphql";
import { useOptionalTabId } from "./useTabId";

type UsePushCandidateNotesParams = {
  callIds: string[];
  /** When set, signals the content script that an interview is selected */
  selectedCallId?: string;
};

/**
 * Format NotesForScorecard response as plain text.
 *
 * Uses the same data source as other ATS integrations (Greenhouse, Lever, etc.):
 * 1. Custom prompt summary (aiScorecardCustomSummary) — already formatted text
 * 2. AI Q&A notes (aiScorecardQuestionNotes) — question/answer pairs
 * 3. Manual user notes (generalNotes + questionNotes) — fallback
 */
const formatScorecardNotesAsText = (data: NotesForScorecardQuery): string => {
  const { call } = data;
  if (!call) return "";

  // 1. Custom prompt summary (already formatted text)
  if (call.aiScorecardCustomSummary) {
    return `BrightHire - AI Notes\n\n${call.aiScorecardCustomSummary}`;
  }

  // 2. AI Q&A notes (same resolver as 1-click scorecard)
  const aiNotes = call.aiScorecardQuestionNotes ?? [];
  if (aiNotes.length > 0) {
    const lines: string[] = ["BrightHire - AI Notes", ""];
    aiNotes.forEach((note) => {
      lines.push(note.aiQuestion);
      note.callAiNoteAnswerItems.forEach((item) => {
        lines.push(`  - ${item.currentText}`);
      });
      lines.push("");
    });
    return lines.join("\n").trim();
  }

  // 3. Manual user notes
  const generalNotes = call.generalNotes ?? [];
  const questionNotes = (call.questions ?? []).flatMap(
    (q) => q.questionNotes ?? []
  );
  const allNotes = [...generalNotes, ...questionNotes];
  if (allNotes.length > 0) {
    const lines: string[] = ["BrightHire - Interview Notes", ""];
    allNotes.forEach((note) => {
      lines.push(`- ${note.text}`);
    });
    return lines.join("\n").trim();
  }

  return "";
};

/**
 * Hook that listens for `pullCandidateNotes` messages from the Workday
 * candidate notes content script and responds with formatted AI notes.
 *
 * Uses the same data source as other ATS integrations (NotesForScorecard).
 */
export const usePushCandidateNotes = ({
  callIds,
  selectedCallId,
}: UsePushCandidateNotesParams): void => {
  const tabId = useOptionalTabId();
  const sendGAEvent = useSendGAEvent();

  // Store the tabUrl received from the content script message
  const tabUrlRef = useRef<string | null>(null);

  const [fetchScorecardNotes] = useNotesForScorecardLazyQuery();

  // Stabilize callIds across renders so the callback doesn't churn
  const callIdsKey = callIds.join(",");
  const stableCallIds = useMemo(() => callIds, [callIdsKey]);

  const handlePullCandidateNotes = useCallback(async () => {
    if (tabId == null) return;

    // Fetch per-call notes (same data source as other ATS integrations)
    const callIdToFetch = selectedCallId ?? stableCallIds[0];
    if (callIdToFetch) {
      try {
        const { data } = await fetchScorecardNotes({
          variables: {
            callId: callIdToFetch,
            currentUserOnly: true,
            tabUrl: tabUrlRef.current,
          },
        });

        if (data) {
          const text = formatScorecardNotesAsText(data);
          if (text) {
            await fillCandidateNotes(tabId, text);
            sendGAEvent(
              "candidate_notes_import",
              "candidate",
              "scorecard_notes"
            );
            return;
          }
        }
      } catch {
        // No notes available
      }
    }

    // 3. Nothing to import
    await fillCandidateNotes(tabId, "");
  }, [selectedCallId, stableCallIds, tabId, fetchScorecardNotes, sendGAEvent]);

  // Notify the content script whether an interview is selected
  useEffect(() => {
    if (tabId == null) return undefined;

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    candidateNotesStatus(tabId, !!selectedCallId).catch(() => {});

    return () => {
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      candidateNotesStatus(tabId, false).catch(() => {});
    };
  }, [tabId, selectedCallId]);

  useEffect(() => {
    if (tabId == null) return undefined;

    const { stopListening } = onExtensionMessage((message) => {
      if (message.action === "pullCandidateNotes") {
        // Capture the tabUrl sent by the content script
        tabUrlRef.current = message.payload?.tabUrl ?? null;
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        handlePullCandidateNotes().catch(() => {});
      }
    });
    return stopListening;
  }, [handlePullCandidateNotes, tabId]);
};
